import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MapComponent, MarkerComponent, PopupComponent } from '@maplibre/ngx-maplibre-gl';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { toSignal } from '@angular/core/rxjs-interop';
import { GatewayRtkConfig, RtkService } from '@wheere/api-gateway';
import { lastValueFrom } from 'rxjs';
import { SITE_CONFIG_WEBSOCKET } from '../data/site-config-websocket.token';
import { SitePositionResult } from './site-position-dialog.component';

interface RTKLivePosition {
  timestamp: string;
  latitude: number;
  longitude: number;
  altitude: number;
  geoidHeight: number;
  fixQuality: number;
  hdop: number;
  satelliteCount: number;
  raw: string;
}

/**
 * Live RTK position picker — re-built with feature parity vs inside `lib-rtk-position-dialog`:
 * <ul>
 *   <li>MapLibre map showing the live position dot, the assigned base, and the gateway's last
 *       known position.</li>
 *   <li>AUTO/MANUAL chip + base id + distance (km / m) + active flag in the header.</li>
 *   <li>NMEA parser (GNGGA) → fix quality, HDOP, sat count.</li>
 *   <li>"Use this position" returns a {@link SitePositionResult} with the geoid-corrected
 *       altitude.</li>
 * </ul>
 *
 * <p>The websocket is injected via the {@link SITE_CONFIG_WEBSOCKET} token so the dialog stays
 * portable between inside and integrator. A maptiles key can be passed via {@link maptilesKey}
 * to use the real Wheere style; otherwise we fall back to the demotiles raster style.
 */
@Component({
  selector: 'lib-site-rtk-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    TagModule,
    ProgressSpinnerModule,
    MapComponent,
    MarkerComponent,
    PopupComponent,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      [header]="'siteRtkDialog.header.title' | transloco"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '95vw', maxWidth: '980px' }"
      (onHide)="onClose()">
      <!-- Header strip: gateway + RTK config (mode / base / distance / active) -->
      <div
        class="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
        @if (gatewayName()) {
          <span class="font-semibold text-gray-700">{{ gatewayName() }}</span>
          <span class="text-gray-400">·</span>
        }
        @if (rtkConfig(); as cfg) {
          <span class="font-semibold text-gray-600">{{ 'siteRtkDialog.header.mode' | transloco }}</span>
          <p-tag
            [severity]="cfg.mode === 'MANUAL' ? 'info' : 'secondary'"
            [value]="
              cfg.mode === 'MANUAL'
                ? ('siteRtkDialog.modes.manual' | transloco)
                : ('siteRtkDialog.modes.auto' | transloco)
            "></p-tag>
          <span class="ml-2 font-semibold text-gray-600">{{ 'siteRtkDialog.header.base' | transloco }}</span>
          @if (cfg.base; as base) {
            <span class="font-mono">{{ base.baseId }}</span>
            @if (base.distanceKm !== null && base.distanceKm !== undefined) {
              <span class="text-gray-500">— {{ formatDistance(base.distanceKm) }}</span>
            }
            <p-tag
              [severity]="base.active ? 'success' : 'danger'"
              [value]="
                base.active
                  ? ('siteRtkDialog.header.active' | transloco)
                  : ('siteRtkDialog.header.inactive' | transloco)
              "></p-tag>
          } @else {
            <span class="italic text-gray-500">{{ 'siteRtkDialog.header.noBase' | transloco }}</span>
          }
        } @else {
          <span class="italic text-gray-500">{{ 'siteRtkDialog.header.configUnavailable' | transloco }}</span>
        }
      </div>

      <div class="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <!-- Map (left, 2/3) -->
        <div class="lg:col-span-2">
          <div class="h-[420px] overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
            @if (mapStyle()) {
              <mgl-map
                [mapStyle]="mapStyle()"
                [zoom]="[mapZoom()]"
                [center]="mapCenter()"
                [maxZoom]="20"
                class="h-full w-full">
                <!-- Gateway last known position (if available, distinct from live RTK) -->
                @if (gatewayPosition(); as gw) {
                  <mgl-marker [lngLat]="[gw.longitude, gw.latitude]" [color]="'#9ca3af'"></mgl-marker>
                }

                <!-- Assigned RTK base -->
                @if (rtkConfig()?.base; as base) {
                  <mgl-marker [lngLat]="[base.lon, base.lat]" [color]="'#5000CD'">
                    <ng-template>
                      <div
                        class="bg-eletricPurple-500 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-white shadow">
                        <i class="pi pi-flag text-xs"></i>
                      </div>
                    </ng-template>
                  </mgl-marker>
                  <mgl-popup [lngLat]="[base.lon, base.lat]" [offset]="14" [closeButton]="false" [closeOnClick]="false">
                    <div class="font-mono text-xs">
                      <div class="text-eletricPurple-700 font-bold">
                        {{ 'siteRtkDialog.popup.base' | transloco }} {{ base.baseId }}
                      </div>
                      <div class="text-gray-500">{{ base.lat.toFixed(5) }}, {{ base.lon.toFixed(5) }}</div>
                    </div>
                  </mgl-popup>
                }

                <!-- Live RTK position -->
                @if (currentPosition(); as p) {
                  <mgl-marker [lngLat]="[p.longitude, p.latitude]" [color]="fixHex(p.fixQuality)">
                    <ng-template>
                      <div
                        class="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-white shadow"
                        [style.background]="fixHex(p.fixQuality)">
                        <span class="h-2 w-2 rounded-full bg-white"></span>
                      </div>
                    </ng-template>
                  </mgl-marker>
                  <mgl-popup
                    [lngLat]="[p.longitude, p.latitude]"
                    [offset]="14"
                    [closeButton]="false"
                    [closeOnClick]="false">
                    <div class="font-mono text-xs">
                      <div class="font-bold">
                        {{ 'siteRtkDialog.popup.rtkPrefix' | transloco }} · {{ fixLabel(p.fixQuality) }}
                      </div>
                      <div class="text-gray-600">{{ p.latitude.toFixed(7) }}, {{ p.longitude.toFixed(7) }}</div>
                      <div class="text-gray-500">
                        {{ 'siteRtkDialog.popup.altShort' | transloco }} {{ correctedAltitude(p).toFixed(2) }}m ·
                        {{ 'siteRtkDialog.popup.hdop' | transloco }} {{ p.hdop.toFixed(2) }}
                      </div>
                    </div>
                  </mgl-popup>
                }
              </mgl-map>
            } @else {
              <div class="flex h-full items-center justify-center text-sm text-gray-500">
                {{ 'siteRtkDialog.map.tilesUnavailable' | transloco }}
              </div>
            }
          </div>
        </div>

        <!-- Right column: live data + GPS info -->
        <div class="flex flex-col gap-3">
          <!-- Live position card -->
          <div class="rounded-xl border border-gray-200 bg-white p-3">
            <div class="mb-2 flex items-center justify-between">
              <span class="text-sm font-semibold uppercase tracking-wider text-gray-500">{{
                'siteRtkDialog.live.title' | transloco
              }}</span>
              @if (sessionActive()) {
                <p-tag
                  [value]="'siteRtkDialog.live.live' | transloco"
                  severity="success"
                  icon="pi pi-circle-fill"></p-tag>
              } @else {
                <p-tag [value]="'siteRtkDialog.live.idle' | transloco" severity="info"></p-tag>
              }
            </div>

            @if (currentPosition(); as p) {
              <div class="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div class="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {{ 'siteRtkDialog.live.latitude' | transloco }}
                  </div>
                  <div class="font-mono">{{ p.latitude.toFixed(7) }}</div>
                </div>
                <div>
                  <div class="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {{ 'siteRtkDialog.live.longitude' | transloco }}
                  </div>
                  <div class="font-mono">{{ p.longitude.toFixed(7) }}</div>
                </div>
                <div>
                  <div class="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {{ 'siteRtkDialog.live.altitudeCorrected' | transloco }}
                  </div>
                  <div class="font-mono">{{ correctedAltitude(p).toFixed(3) }} m</div>
                </div>
                <div>
                  <div class="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {{ 'siteRtkDialog.live.quality' | transloco }}
                  </div>
                  <p-tag [value]="fixLabel(p.fixQuality)" [severity]="fixSeverity(p.fixQuality)"></p-tag>
                </div>
                <div>
                  <div class="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {{ 'siteRtkDialog.live.hdop' | transloco }}
                  </div>
                  <div class="font-mono">{{ p.hdop.toFixed(2) }}</div>
                </div>
                <div>
                  <div class="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    {{ 'siteRtkDialog.live.sats' | transloco }}
                  </div>
                  <div class="font-mono">{{ p.satelliteCount }}</div>
                </div>
              </div>
              <div class="mt-2 text-xs text-gray-500">
                {{ 'siteRtkDialog.live.nmeaTimestamp' | transloco }} {{ p.timestamp }}
              </div>
            } @else if (sessionActive()) {
              <div class="flex items-center gap-2 text-sm text-gray-500">
                <p-progress-spinner styleClass="w-4 h-4" strokeWidth="6"></p-progress-spinner>
                {{ 'siteRtkDialog.live.waitingNmea' | transloco }}
              </div>
            } @else {
              <div class="text-sm text-gray-500">
                {{ 'siteRtkDialog.live.startHint' | transloco }}
                <code class="font-mono">/topic/gateway/{{ gatewayId() }}/rtk</code>.
              </div>
            }
          </div>

          <!-- Gateway last known position from RTK config (separate from live RTK) -->
          @if (gatewayPosition(); as gw) {
            <div class="rounded-xl border border-gray-200 bg-white p-3">
              <div class="mb-2 text-sm font-semibold uppercase tracking-wider text-gray-500">
                {{ 'siteRtkDialog.gatewayLastFix.title' | transloco }}
              </div>
              <div class="font-mono text-sm text-gray-700">
                {{ gw.latitude.toFixed(6) }}, {{ gw.longitude.toFixed(6) }}
              </div>
              @if (gw.altitude !== null && gw.altitude !== undefined) {
                <div class="mt-1 text-xs text-gray-500">
                  {{ 'siteRtkDialog.popup.altShort' | transloco }} {{ gw.altitude.toFixed(2) }} m
                </div>
              }
            </div>
          }
        </div>
      </div>

      <ng-template #footer>
        <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
          @if (!sessionActive()) {
            <p-button
              [label]="'siteRtkDialog.actions.startSession' | transloco"
              icon="pi pi-play"
              (click)="startSession()"></p-button>
          } @else {
            <p-button
              [label]="'siteRtkDialog.actions.stop' | transloco"
              icon="pi pi-stop"
              severity="secondary"
              (click)="stopSession()"></p-button>
          }
          <p-button
            [label]="'siteRtkDialog.actions.usePosition' | transloco"
            icon="pi pi-check"
            severity="success"
            [disabled]="!currentPosition()"
            (click)="usePosition()"></p-button>
          <p-button
            [label]="'siteRtkDialog.actions.cancel' | transloco"
            icon="pi pi-times"
            severity="secondary"
            [outlined]="true"
            (click)="onClose()"></p-button>
        </div>
      </ng-template>
    </p-dialog>
  `,
})
export class SiteRtkDialogComponent implements OnInit, OnDestroy {
  visible = model<boolean>(false);
  gatewayId = model<string | null>(null);
  gatewayName = model<string>('');
  /** When `true`, the result emits an {@link SitePositionResult} with antennaHeight set to the corrected altitude (tracker mode). */
  asTracker = model<boolean>(false);
  /** MapTiler key from the host app — empty falls back to the OSM raster style. */
  maptilesKey = input<string>('');

  positionPicked = output<SitePositionResult>();

  protected readonly sessionActive = signal(false);
  protected readonly currentPosition = signal<RTKLivePosition | null>(null);
  protected readonly rtkConfig = signal<GatewayRtkConfig | null>(null);

  /** Read the gateway's last known position out of the RTK config (separate from live NMEA).
   *  The {@code GpsPosition} type uses `lat`/`lon` (not latitude/longitude). */
  protected readonly gatewayPosition = computed<{ latitude: number; longitude: number; altitude?: number } | null>(
    () => {
      const cfg = this.rtkConfig();
      const pos = cfg?.position;
      if (!pos || pos.lat == null || pos.lon == null) return null;
      return { latitude: pos.lat, longitude: pos.lon };
    }
  );

  protected readonly mapStyle = computed(() => {
    const key = this.maptilesKey();
    if (key) {
      return `https://api.maptiler.com/maps/streets-v2/style.json?key=${key}`;
    }
    return 'https://demotiles.maplibre.org/style.json';
  });

  protected readonly mapCenter = computed<[number, number] | undefined>(() => {
    // Prefer the live RTK fix, fall back to the assigned base, then to the gateway position.
    const p = this.currentPosition();
    if (p) return [p.longitude, p.latitude];
    const base = this.rtkConfig()?.base;
    if (base) return [base.lon, base.lat];
    const gw = this.gatewayPosition();
    if (gw) return [gw.longitude, gw.latitude];
    return [2.349014, 48.864716];
  });

  protected readonly mapZoom = signal(17);

  #ws = inject(SITE_CONFIG_WEBSOCKET, { optional: true });
  #rtkService = inject(RtkService);
  #cdr = inject(ChangeDetectorRef);
  #transloco = inject(TranslocoService);
  // Re-derive translated fix labels whenever the active language changes.
  protected readonly activeLang = toSignal(this.#transloco.langChanges$, {
    initialValue: this.#transloco.getActiveLang(),
  });

  private subscription: { unsubscribe(): void } | null = null;

  async ngOnInit(): Promise<void> {
    await this.loadConfig();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private async loadConfig(): Promise<void> {
    const id = this.gatewayId();
    if (!id) return;
    try {
      const cfg = await lastValueFrom(this.#rtkService.getGatewayRtk(id));
      this.rtkConfig.set(cfg);
      this.#cdr.detectChanges();
    } catch {
      // Non-blocking — operator can still start a session and pick a position.
    }
  }

  protected formatDistance(km: number | string | null | undefined): string {
    if (km == null) return '';
    const n = typeof km === 'number' ? km : Number(km);
    if (!Number.isFinite(n)) return '';
    return n < 1 ? `${Math.round(n * 1000)} m` : `${n.toFixed(1)} km`;
  }

  protected async startSession(): Promise<void> {
    const id = this.gatewayId();
    if (!id || !this.#ws) return;

    try {
      await this.#ws.initializeStomp();
      this.subscription = this.#ws.subscribe<string>(`/topic/gateway/${id}/rtk`, (nmea: string) => {
        const parsed = this.parseNMEA(nmea);
        if (parsed) {
          this.currentPosition.set(parsed);
          this.#cdr.detectChanges();
        }
      });
      this.sessionActive.set(true);
    } catch (e) {
      console.error('Failed to start RTK session', e);
    }
  }

  protected stopSession(): void {
    this.cleanup();
    this.sessionActive.set(false);
  }

  protected usePosition(): void {
    const p = this.currentPosition();
    if (!p) return;
    const correctedAlt = this.correctedAltitude(p);
    const result: SitePositionResult = {
      lat: p.latitude,
      lng: p.longitude,
      altitude: correctedAlt,
    };
    if (this.asTracker()) {
      result.antennaHeight = correctedAlt;
    }
    this.positionPicked.emit(result);
    this.cleanup();
    this.visible.set(false);
  }

  protected onClose(): void {
    this.cleanup();
    this.visible.set(false);
  }

  protected correctedAltitude(p: RTKLivePosition): number {
    return p.altitude + p.geoidHeight;
  }

  protected fixLabel(q: number): string {
    // Reading activeLang() ensures the computed/template re-evaluates on language change.
    this.activeLang();
    switch (q) {
      case 0:
        return this.#transloco.translate('siteRtkDialog.fix.invalid');
      case 1:
        return this.#transloco.translate('siteRtkDialog.fix.gps');
      case 2:
        return this.#transloco.translate('siteRtkDialog.fix.dgps');
      case 4:
        return this.#transloco.translate('siteRtkDialog.fix.rtkFixed');
      case 5:
        return this.#transloco.translate('siteRtkDialog.fix.rtkFloat');
      default:
        return this.#transloco.translate('siteRtkDialog.fix.unknown');
    }
  }

  protected fixSeverity(q: number): 'success' | 'warn' | 'danger' | 'info' {
    switch (q) {
      case 4:
        return 'success';
      case 5:
        return 'warn';
      case 1:
      case 2:
        return 'danger';
      default:
        return 'info';
    }
  }

  protected fixHex(q: number): string {
    switch (q) {
      case 4:
        return '#22c55e';
      case 5:
        return '#f59e0b';
      case 1:
      case 2:
        return '#ef4444';
      default:
        return '#6b7280';
    }
  }

  private cleanup(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
  }

  private parseNMEA(nmea: string): RTKLivePosition | null {
    if (!nmea || typeof nmea !== 'string') return null;
    const parts = nmea.split(',');
    if (parts.length < 15 || !parts[0].includes('GGA')) return null;
    const [, timestamp, latRaw, latDir, lngRaw, lngDir, fixStr, satStr, hdopStr, altStr, , geoidStr] = parts;
    if (!latRaw || !lngRaw || !latDir || !lngDir) return null;
    const latNum = parseFloat(latRaw);
    const lngNum = parseFloat(lngRaw);
    if (isNaN(latNum) || isNaN(lngNum)) return null;
    const latitude = this.toDecimal(latNum, latDir);
    const longitude = this.toDecimal(lngNum, lngDir);
    if (latitude === 0 && longitude === 0) return null;
    return {
      timestamp: timestamp || '',
      latitude,
      longitude,
      altitude: parseFloat(altStr) || 0,
      geoidHeight: parseFloat(geoidStr) || 0,
      fixQuality: parseInt(fixStr) || 0,
      hdop: parseFloat(hdopStr) || 99.99,
      satelliteCount: parseInt(satStr) || 0,
      raw: nmea,
    };
  }

  private toDecimal(coord: number, dir: string): number {
    const deg = Math.floor(coord / 100);
    const min = coord % 100;
    let dec = deg + min / 60;
    if (dir === 'S' || dir === 'W') dec = -dec;
    return dec;
  }
}
